import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const PaymentSourceManager = ({ sources }) => {
  const [paymentSources, setPaymentSources] = useState(sources);

  const toggleSource = (id) => {
    setPaymentSources(prev => 
      prev?.map(source => 
        source?.id === id ? { ...source, enabled: !source?.enabled } : source
      )
    );
  };

  const moveSourceUp = (index) => {
    if (index === 0) return;
    const newSources = [...paymentSources];
    [newSources[index - 1], newSources[index]] = [newSources?.[index], newSources?.[index - 1]];
    setPaymentSources(newSources?.map((source, idx) => ({ ...source, priority: idx + 1 })));
  };

  const moveSourceDown = (index) => {
    if (index === paymentSources?.length - 1) return;
    const newSources = [...paymentSources];
    [newSources[index], newSources[index + 1]] = [newSources?.[index + 1], newSources?.[index]];
    setPaymentSources(newSources?.map((source, idx) => ({ ...source, priority: idx + 1 })));
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Payment Sources</h2>
          <p className="card-description">Manage funding priority and availability</p>
        </div>
      </div>
      <div className="card-content">
        <div className="space-y-3">
          {paymentSources?.map((source, index) => (
            <div 
              key={source?.id}
              className={`p-4 rounded-lg border-2 transition-all ${
                source?.enabled 
                  ? 'border-border bg-card hover:border-primary/50' :'border-border/50 bg-muted/30 opacity-60'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: `${source?.color}15` }}
                  >
                    <Icon name={source?.icon} size={20} color={source?.color} />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">{source?.name}</h4>
                    <p className="text-xs text-muted-foreground">{source?.symbol}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleSource(source?.id)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      source?.enabled ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        source?.enabled ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-bold text-foreground">
                    ${source?.usdValue?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {source?.balance?.toLocaleString('en-US', { maximumFractionDigits: 4 })} {source?.symbol}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveSourceUp(index)}
                    disabled={index === 0}
                    className="p-1 hover:bg-muted rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Icon name="ChevronUp" size={16} className="text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => moveSourceDown(index)}
                    disabled={index === paymentSources?.length - 1}
                    className="p-1 hover:bg-muted rounded disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
                  </button>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-border">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Priority</span>
                  <span className="font-semibold text-foreground">#{source?.priority}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 p-3 bg-muted/50 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Card payments will automatically use sources in priority order. Drag to reorder or toggle to enable/disable.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSourceManager;